import { scene2IconToImages } from "../video-replace/scene-2";
import { deviceIsDesktop } from "../common/checkDevice";
import { appendSvgCurveTop } from "../../utils/appendSvgCurve";

export function scene_2_rolling_bottle_and_icons() {
  const root = document.querySelector(".scene-2");
  if (!root || root.dataset.bound === "1") return;
  root.dataset.bound = "1";

  gsap.registerPlugin(ScrollTrigger);

  // 1) Build icon+text containers (images instead of videos)
  scene2IconToImages();
  appendSvgCurveTop(".scene-2");

  // Heading baseline (runs once)
  gsap.set(".scene-2 h4", { opacity: 1 });
  gsap
    .timeline({
      scrollTrigger: {
        trigger: ".scene-2 .article_block_text",
        start: "top 80%",
        once: true,
      },
    })
    .from(".scene-2 h4 .char", {
      opacity: 0,
      y: 10,
      duration: 0.02,
      stagger: 0.03,
      ease: "power1.out",
    });

  // 2) Bottle rotation (shared for all breakpoints)
  const imageCount = 120;
  const bottleImg = document.querySelector(".bottle-3-d img");
  if (bottleImg) {
    const path = (i) => `/uploads/images/3d/jr-dark/${String(i).padStart(2, "0")}.webp`;
    const wrapIdx = gsap.utils.wrap(0, imageCount);
    const LOOPS = 6;
    const START_INDEX = deviceIsDesktop() ? 57 : 82;

    const frames = Array.from({ length: imageCount }, (_, i) => {
      const im = new Image();
      im.src = path(i);
      return im;
    });

    let target = START_INDEX,
      current = START_INDEX;

    (function raf() {
      if (current !== target) {
        current = target;
        if (frames[current]) bottleImg.src = frames[current].src;
      }
      requestAnimationFrame(raf);
    })();

    ScrollTrigger.create({
      trigger: ".scene-2",
      start: "top bottom",
      end: "max",
      scrub: true,
      onUpdate: (self) => {
        const raw = Math.floor(self.progress * LOOPS * imageCount);
        target = wrapIdx(raw + START_INDEX);
      },
    });

    gsap
      .timeline({
        scrollTrigger: {
          trigger: ".bottle-3-d",
          start: "top 70%",
          once: true,
        },
      })
      .to(".scene-2 .article_block_item_img", { opacity: 1, y: 0 });
  }

  // 3) Icons (image sequences) per paragraph
  const ICONS = [
    {
      basePath: "/custom/images/animations-image-seq/hand/",
      total: 43,
      fps: 36,
      pad: 2,
      ext: "webp",
    },
    {
      basePath: "/custom/images/animations-image-seq/arrow/",
      total: 34,
      fps: 36,
      pad: 2,
      ext: "webp",
    },
    {
      basePath: "/custom/images/animations-image-seq/star/",
      total: 30,
      fps: 36,
      pad: 2,
      ext: "webp",
    },
  ];

  const blocks = gsap.utils.toArray(".scene-2 .scene-2-text-container");

  const controllers = blocks
    .map((wrap, i) => {
      const cfg = ICONS[i];
      const img = wrap.querySelector(".scene-2-icon");
      const p = wrap.querySelector("p");
      if (!cfg || !img || !p) return null;

      // initial state
      gsap.set(img, { opacity: 0, x: -12, willChange: "transform,opacity" });
      gsap.set(p, { opacity: 0, y: 20, willChange: "transform,opacity" });

      const seq = window.makeImageSequence?.({
        target: img,
        totalFrames: cfg.total,
        fps: cfg.fps,
        mode: "once",
        basePath: cfg.basePath,
        pad: cfg.pad,
        ext: cfg.ext,
      });

      return { wrap, p, img, seq, revealed: false };
    })
    .filter(Boolean);

  // ------------------------------
  // Helper: simple per-block logic
  // ------------------------------
  function setupSimpleControllers() {
    const triggers = [];

    controllers.forEach((c) => {
      gsap.set(c.img, { opacity: 0, x: -12 });
      gsap.set(c.p, { opacity: 0, y: 20, pointerEvents: "none" });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: c.wrap,
          start: "top 80%",
          toggleActions: "play none none none",
          once: true,
        },
      });

      tl.to(c.img, {
        opacity: 1,
        x: 0,
        duration: 0.35,
        ease: "power1.out",
      })
        .to(
          c.p,
          {
            opacity: 1,
            y: 0,
            duration: 0.45,
            ease: "power1.out",
            onComplete: () => {
              c.p.style.pointerEvents = "auto";
            },
          },
          "<0.05"
        )
        .add(() => c.seq?.play?.(), ">-0.1");

      triggers.push(tl.scrollTrigger);
    });

    // cleanup kills all simple triggers
    return () => {
      triggers.forEach((st) => st && st.kill());
    };
  }

  // ------------------------------
  // MATCH MEDIA: desktop vs mobile
  // ------------------------------
  ScrollTrigger.matchMedia({
    // MOBILE & small width: always simple, no pin
    "(max-width: 767px)": () => {
      return setupSimpleControllers();
    },

    // DESKTOP width: heavy pin only on *real* desktop, otherwise simple
    "(min-width: 768px)": () => {
      const isRealDesktop = deviceIsDesktop() && !ScrollTrigger.isTouch;

      if (!isRealDesktop) {
        // iPad / big touch / touch laptops → avoid pinned heavy scene
        return setupSimpleControllers();
      }

      const PX_PER_SEGMENT = 450;
      const EXTRA_SCROLL = 400;
      const len = controllers.length;
      if (!len) return () => {};

      // helper so both STs share the same end expression,
      // no dependency on another trigger instance
      const getEndValue = () => `+=${controllers.length * PX_PER_SEGMENT + EXTRA_SCROLL}`;

      // reset for this mode
      controllers.forEach((c) => {
        c.revealed = false;
        gsap.set(c.img, { opacity: 0, x: -12 });
        gsap.set(c.p, { opacity: 0, y: 20, pointerEvents: "none" });
      });

      // Pinned bottle section
      const pinST = ScrollTrigger.create({
        trigger: ".bottle-3-d",
        start: "top top",
        end: getEndValue, // ✅ function, independent of pinST
        pin: true,
        scrub: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      });

      const totalScroll = controllers.length * PX_PER_SEGMENT + EXTRA_SCROLL;
      const thresholds = Array.from({ length: len }, (_, i) => (i * PX_PER_SEGMENT) / totalScroll);

      const progST = ScrollTrigger.create({
        trigger: ".bottle-3-d",
        start: "top top",
        end: getEndValue, // ✅ same function, no .end chain
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const prog = self.progress;
          let idx = 0;
          for (let i = 0; i < thresholds.length; i++) {
            if (prog >= thresholds[i]) idx = i;
          }
          revealIndex(idx);
        },
      });

      function revealIndex(i) {
        const c = controllers[i];
        if (!c || c.revealed) return;
        c.revealed = true;

        gsap.to(c.p, {
          opacity: 1,
          y: 0,
          duration: 0.45,
          ease: "power1.out",
          onComplete: () => {
            c.p.style.pointerEvents = "auto";
          },
        });

        gsap.to(c.img, {
          opacity: 1,
          x: 0,
          duration: 0.35,
          ease: "power1.out",
        });

        c.seq?.play?.();
      }

      // explicit cleanup on breakpoint change
      return () => {
        pinST.kill();
        progST.kill();
        controllers.forEach((c) => {
          c.revealed = false;
          gsap.set(c.img, { opacity: 0, x: -12 });
          gsap.set(c.p, { opacity: 0, y: 20, pointerEvents: "none" });
        });
      };
    },
  });
}

// import { scene2IconToImages } from "../video-replace/scene-2";
// import { deviceIsDesktop } from "../common/checkDevice";
// import { appendSvgCurveTop } from "../../utils/appendSvgCurve";

// export function scene_2_rolling_bottle_and_icons() {
//   const root = document.querySelector(".scene-2");
//   if (!root || root.dataset.bound === "1") return;
//   root.dataset.bound = "1";

//   gsap.registerPlugin(ScrollTrigger);

//   // 1) Build icon+text containers (images instead of videos)
//   scene2IconToImages();
//   appendSvgCurveTop(".scene-2");

//   // Heading baseline
//   gsap.set(".scene-2 h4", { opacity: 1 });
//   gsap
//     .timeline({
//       scrollTrigger: { trigger: ".scene-2 .article_block_text", start: "top 80%", once: true },
//     })
//     .from(".scene-2 h4 .char", {
//       opacity: 0,
//       y: 10,
//       duration: 0.02,
//       stagger: 0.03,
//       ease: "power1.out",
//     });

//   // 2) Bottle rotation (unchanged)
//   const imageCount = 120;
//   const bottleImg = document.querySelector(".bottle-3-d img");
//   if (bottleImg) {
//     const path = (i) => `/uploads/images/3d/jr-dark/${String(i).padStart(2, "0")}.webp`;
//     const wrapIdx = gsap.utils.wrap(0, imageCount);
//     const LOOPS = 6;
//     const START_INDEX = deviceIsDesktop() ? 57 : 82;

//     const frames = Array.from({ length: imageCount }, (_, i) => {
//       const im = new Image();
//       im.src = path(i);
//       return im;
//     });

//     let target = START_INDEX,
//       current = START_INDEX;
//     (function raf() {
//       if (current !== target) {
//         current = target;
//         if (frames[current]) bottleImg.src = frames[current].src;
//       }
//       requestAnimationFrame(raf);
//     })();

//     ScrollTrigger.create({
//       trigger: ".scene-2",
//       start: "top bottom",
//       end: "max",
//       scrub: true,
//       onUpdate: (self) => {
//         const raw = Math.floor(self.progress * LOOPS * imageCount);
//         target = wrapIdx(raw + START_INDEX);
//       },
//     });

//     gsap
//       .timeline({
//         scrollTrigger: { trigger: ".bottle-3-d", start: "top 70%", once: true },
//       })
//       .to(".scene-2 .article_block_item_img", { opacity: 1, y: 0 });
//   }

//   // 3) Icons (image sequences) per paragraph
//   //    Order matters: one config per paragraph
//   const ICONS = [
//     { basePath: "/custom/images/animations-image-seq/hand/", total: 43, fps: 36, pad: 2, ext: "webp" },
//     { basePath: "/custom/images/animations-image-seq/arrow/", total: 34, fps: 36, pad: 2, ext: "webp" },
//     { basePath: "/custom/images/animations-image-seq/star/", total: 30, fps: 36, pad: 2, ext: "webp" },
//   ];

//   const blocks = gsap.utils.toArray(".scene-2 .scene-2-text-container");

//   // Build controllers for each block
//   const controllers = blocks
//     .map((wrap, i) => {
//       const cfg = ICONS[i];
//       const img = wrap.querySelector(".scene-2-icon");
//       const p = wrap.querySelector("p");
//       if (!cfg || !img || !p) return null;

//       // Initial visual states
//       gsap.set(img, { opacity: 0, x: -12, willChange: "transform,opacity" });
//       gsap.set(p, { opacity: 0, y: 20, willChange: "transform,opacity" });

//       // Image-sequence controller (don’t autoplay)
//       const seq = window.makeImageSequence?.({
//         target: img,
//         totalFrames: cfg.total,
//         fps: cfg.fps,
//         mode: "once",
//         basePath: cfg.basePath,
//         pad: cfg.pad,
//         ext: cfg.ext,
//       });

//       return { wrap, p, img, seq, revealed: false };
//     })
//     .filter(Boolean);

//   // ============================ CHANGED FOR MATCH MEDIA

//   function setupSimpleControllers() {
//     // simple per-block reveal (no pin, no heavy stuff)
//     controllers.forEach((c) => {
//       // reset visuals
//       gsap.set(c.img, { opacity: 0, x: -12 });
//       gsap.set(c.p, { opacity: 0, y: 20, pointerEvents: "none" });

//       const tl = gsap.timeline({
//         scrollTrigger: {
//           trigger: c.wrap,
//           start: "top 80%",
//           toggleActions: "play none none none",
//           once: true,
//         },
//       });

//       tl.to(c.img, {
//         opacity: 1,
//         x: 0,
//         duration: 0.35,
//         ease: "power1.out",
//       })
//         .to(
//           c.p,
//           {
//             opacity: 1,
//             y: 0,
//             duration: 0.45,
//             ease: "power1.out",
//             onComplete: () => {
//               c.p.style.pointerEvents = "auto";
//             },
//           },
//           "<0.05"
//         )
//         .add(() => c.seq?.play?.(), ">-0.1");
//     });

//     // optional cleanup hook; ScrollTrigger will auto-revert
//     return () => {};
//   }

//   ScrollTrigger.matchMedia({
//     // ===== MOBILE WIDTH: always simple =====
//     "(max-width: 767px)": () => {
//       return setupSimpleControllers();
//     },

//     // ===== DESKTOP WIDTH: only heavy if REALLY desktop =====
//     "(min-width: 768px)": () => {
//       const isRealDesktop = deviceIsDesktop() && !ScrollTrigger.isTouch;

//       if (!isRealDesktop) {
//         // iPad, big touch tablet, or touch laptop → use light/mobile logic
//         return setupSimpleControllers();
//       }

//       // ==== REAL DESKTOP: pinned scroll logic ====
//       const PX_PER_SEGMENT = 450;
//       const EXTRA_SCROLL = 400;
//       const len = controllers.length;
//       const totalScroll = len * PX_PER_SEGMENT + EXTRA_SCROLL;

//       // reset state for this breakpoint
//       controllers.forEach((c) => {
//         c.revealed = false;
//         gsap.set(c.img, { opacity: 0, x: -12 });
//         gsap.set(c.p, { opacity: 0, y: 20, pointerEvents: "none" });
//       });

//       const pinST = ScrollTrigger.create({
//         trigger: ".bottle-3-d",
//         start: "top top",
//         end: `+=${totalScroll}`,
//         pin: true,
//         scrub: true,
//         anticipatePin: 1,
//         invalidateOnRefresh: true,
//       });

//       const thresholds = Array.from({ length: len }, (_, i) => (i * PX_PER_SEGMENT) / totalScroll);

//       ScrollTrigger.create({
//         trigger: ".bottle-3-d",
//         start: "top top",
//         end: () => pinST.end,
//         scrub: true,
//         invalidateOnRefresh: true,
//         onUpdate: (self) => {
//           const prog = self.progress;
//           let idx = 0;
//           for (let i = 0; i < thresholds.length; i++) {
//             if (prog >= thresholds[i]) idx = i;
//           }
//           revealIndex(idx);
//         },
//       });

//       function revealIndex(i) {
//         const c = controllers[i];
//         if (!c || c.revealed) return;
//         c.revealed = true;

//         gsap.to(c.p, {
//           opacity: 1,
//           y: 0,
//           duration: 0.45,
//           ease: "power1.out",
//           onComplete: () => {
//             c.p.style.pointerEvents = "auto";
//           },
//         });

//         gsap.to(c.img, {
//           opacity: 1,
//           x: 0,
//           duration: 0.35,
//           ease: "power1.out",
//         });

//         c.seq?.play?.();
//       }

//       // cleanup when leaving desktop breakpoint (GSAP auto-kills triggers)
//       return () => {};
//     },
//   });
//   // ============================ CHANGED FOR MATCH MEDIA END . WORKING CODE BELOW BEFORE CHANGE

//   // const isDesktop = deviceIsDesktop();

//   // if (isDesktop) {
//   //   // ==== Desktop: pin + reveal one-by-one along scroll (like before) ====
//   //   const PX_PER_SEGMENT = 450; // how much scroll per paragraph
//   //   const EXTRA_SCROLL = 400; // a little extra runway after last
//   //   const len = controllers.length;

//   //   if (len) {
//   //     // Pin the bottle section and drive reveals by progress
//   //     const pinST = ScrollTrigger.create({
//   //       trigger: ".bottle-3-d",
//   //       start: "top top",
//   //       end: `+=${len * PX_PER_SEGMENT + EXTRA_SCROLL}`,
//   //       pin: true,
//   //       scrub: true,
//   //       anticipatePin: 1,
//   //       invalidateOnRefresh: true,
//   //       onEnter: () => {
//   //         // reset visuals when entering pin range
//   //         controllers.forEach((c) => {
//   //           c.revealed = false;
//   //           gsap.set(c.img, { opacity: 0, x: -12 });
//   //           gsap.set(c.p, { opacity: 0, y: 20, pointerEvents: "none" });
//   //         });
//   //         // reveal first immediately for nicer feel
//   //         revealIndex(0);
//   //       },
//   //     });

//   //     // Progress watcher: reveal at thresholds
//   //     const thresholds = Array.from({ length: len }, (_, i) => (i * PX_PER_SEGMENT) / (len * PX_PER_SEGMENT + EXTRA_SCROLL));

//   //     ScrollTrigger.create({
//   //       trigger: ".bottle-3-d",
//   //       start: "top top",
//   //       end: () => pinST.end,
//   //       scrub: true,
//   //       onUpdate: (self) => {
//   //         const prog = self.progress;
//   //         // find highest index whose threshold is passed
//   //         let idx = 0;
//   //         for (let i = 0; i < thresholds.length; i++) {
//   //           if (prog >= thresholds[i]) idx = i;
//   //         }
//   //         revealIndex(idx);
//   //       },
//   //       onRefresh: () => {
//   //         // keep already-revealed in place on refresh
//   //         controllers.forEach((c, i) => {
//   //           if (c.revealed) {
//   //             gsap.set(c.img, { opacity: 1, x: 0 });
//   //             gsap.set(c.p, { opacity: 1, y: 0, pointerEvents: "auto" });
//   //           }
//   //         });
//   //       },
//   //     });

//   //     function revealIndex(i) {
//   //       const c = controllers[i];
//   //       if (!c || c.revealed) return;
//   //       c.revealed = true;

//   //       // text in
//   //       gsap.to(c.p, {
//   //         opacity: 1,
//   //         y: 0,
//   //         duration: 0.45,
//   //         ease: "power1.out",
//   //         onComplete: () => {
//   //           c.p.style.pointerEvents = "auto";
//   //         },
//   //       });

//   //       // icon fade + play sequence once
//   //       gsap.to(c.img, { opacity: 1, x: 0, duration: 0.35, ease: "power1.out" });
//   //       c.seq?.play?.();
//   //     }
//   //   }
//   // } else {
//   //   // ==== Mobile: simple per-block reveal (no pin) ====
//   //   controllers.forEach((c) => {
//   //     const tl = gsap.timeline({
//   //       scrollTrigger: {
//   //         trigger: c.wrap,
//   //         start: "top 80%",
//   //         toggleActions: "play none none none",
//   //         once: true,
//   //       },
//   //     });
//   //     tl.to(c.img, { opacity: 1, x: 0, duration: 0.35, ease: "power1.out" })
//   //       .to(c.p, { opacity: 1, y: 0, duration: 0.45, ease: "power1.out" }, "<0.05")
//   //       .add(() => c.seq?.play?.(), ">-0.1");
//   //   });
//   // }
// }
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// code before changing to images

// =======================
// =======================
// =======================
// =======================
// =======================
// =======================
// =======================
// =======================
// =======================
// =======================

// import { scene2IconToVideo } from "../video-replace/scene-2";
// import { deviceIsDesktop } from "../common/checkDevice";
// import { appendSvgCurveTop } from "../../utils/appendSvgCurve";
// export function scene_2_rolling_bottle_and_icons() {
//   const sceneCheck = document.querySelector(".scene-2");

//   if (!sceneCheck) return;
//   scene2IconToVideo();
//   appendSvgCurveTop(".scene-2");
//   gsap.registerPlugin(ScrollTrigger);

//   const isDesktop = deviceIsDesktop();
//   // --- Heading reveal (once) ---
//   // const split = new SplitText(".scene-2 h4", { types: "chars" });
//   // gsap.set(split.chars, { opacity: 0 });
//   gsap.set(".scene-2 h4", { opacity: 1 });
//   if (isDesktop) {
//     // --- Smooth bottle frames ---
//     const imageCount = 120;
//     const img = document.querySelector(".bottle-3-d img");
//     const imagePath = (i) => `/uploads/images/3d/jr-dark/${i.toString().padStart(2, "0")}.webp`;
//     const wrap = gsap.utils.wrap(0, imageCount);

//     const LOOPS = 6;
//     const START_INDEX = 57;

//     const frames = Array.from({ length: imageCount }, (_, i) => {
//       const im = new Image();
//       im.src = imagePath(i);
//       return im;
//     });

//     let targetIndex = START_INDEX;
//     let currentIndex = START_INDEX;
//     requestAnimationFrame(function raf() {
//       if (currentIndex !== targetIndex) {
//         currentIndex = targetIndex;
//         if (frames[currentIndex] && img) img.src = frames[currentIndex].src;
//       }
//       requestAnimationFrame(raf);
//     });

//     ScrollTrigger.create({
//       trigger: ".scene-2",
//       start: "top bottom",
//       end: "max",
//       scrub: true,
//       onUpdate: (self) => {
//         const rawIndex = Math.floor(self.progress * LOOPS * imageCount);
//         targetIndex = wrap(rawIndex + START_INDEX);
//       },
//     });

//     gsap
//       .timeline({
//         scrollTrigger: { trigger: ".bottle-3-d", start: "top 70%", once: true },
//       })
//       .to(".scene-2 .article_block_item_img", { opacity: 1, y: 0 })
//       // .to(split.chars, { opacity: 1, duration: 0.05, stagger: 0.05, ease: "none" })
//       .from(".scene-2 h4 .char", {
//         opacity: 0,
//         y: 10,
//         duration: 0.02,
//         stagger: 0.03,
//         ease: "power1.out",
//       });

//     // --- Items (videos + paragraphs) ---
//     function getItems() {
//       return gsap.utils.toArray(".scene-2 .scene-2-text-container");
//     }

//     // Initial states for current DOM
//     function initItemStates() {
//       const els = getItems();
//       els.forEach((el) => {
//         const v = el.querySelector("video");
//         const p = el.querySelector("p");
//         if (v) gsap.set(v, { opacity: 0, x: -20 });
//         if (p) gsap.set(p, { opacity: 0, y: 20, pointerEvents: "none" });
//       });
//     }

//     initItemStates();

//     const PX_PER_SEGMENT = 450;
//     const EXTRA_SCROLL = 400;

//     // thresholds builder with guards
//     function buildThresholds() {
//       const els = getItems();
//       const len = els.length || 0;
//       if (!len) return [];
//       const totalScroll = len * PX_PER_SEGMENT + EXTRA_SCROLL;
//       return els.map((_, i) => (i * PX_PER_SEGMENT) / totalScroll);
//     }

//     let revealedFlags = []; // resized with items length
//     let thresholds = buildThresholds();

//     // reveal safely by index
//     function revealOnce(i) {
//       const els = getItems();
//       const len = els.length || 0;
//       if (!len || i < 0 || i >= len) return;

//       // keep flags in sync with current length
//       if (revealedFlags.length !== len) {
//         const old = revealedFlags;
//         revealedFlags = new Array(len).fill(false);
//         for (let k = 0; k < Math.min(old.length, len); k++) revealedFlags[k] = old[k];
//       }
//       if (revealedFlags[i]) return;
//       revealedFlags[i] = true;

//       const el = els[i];
//       const v = el?.querySelector("video");
//       const p = el?.querySelector("p");

//       if (v) {
//         try {
//           if (v.paused) {
//             v.currentTime = 0;
//             v.play()?.catch(() => {});
//           }
//         } catch {}
//         gsap.to(v, { opacity: 1, x: 0, duration: 0.45, ease: "none" });
//       }
//       if (p) {
//         gsap.to(p, { opacity: 1, y: 0, pointerEvents: "auto", duration: 0.55, ease: "none" });
//       }
//     }

//     // Skip pin if there are no items (prevents .length crashes)
//     if (!getItems().length) {
//       // nothing to reveal; you can bail here or just avoid setting STs
//     } else {
//       const st = ScrollTrigger.create({
//         trigger: ".bottle-3-d",
//         start: "top top",
//         end: () => {
//           const len = getItems().length || 0;
//           return `+=${len * PX_PER_SEGMENT + EXTRA_SCROLL}`;
//         },
//         pin: true,
//         scrub: true,
//         anticipatePin: 1,
//         invalidateOnRefresh: true,

//         onEnter: () => {
//           // DOM may have changed; rebuild
//           initItemStates();
//           thresholds = buildThresholds();
//           revealedFlags = new Array(getItems().length).fill(false);
//           if (getItems().length > 0) revealOnce(0);
//         },
//       });

//       ScrollTrigger.create({
//         trigger: ".bottle-3-d",
//         start: "top top",
//         end: () => st.end, // match the pin
//         scrub: true,
//         onUpdate: (self) => {
//           const els = getItems();
//           const len = els.length || 0;
//           if (!len) return;
//           const th = thresholds.length ? thresholds : (thresholds = buildThresholds());
//           const prog = self.progress;
//           for (let i = 0; i < th.length; i++) {
//             if (prog >= th[i]) revealOnce(i);
//           }
//         },
//         onRefresh: () => {
//           initItemStates();
//           thresholds = buildThresholds();
//           // keep already-revealed where possible
//           const len = getItems().length || 0;
//           const old = revealedFlags;
//           revealedFlags = new Array(len).fill(false);
//           for (let i = 0; i < Math.min(old.length, len); i++) revealedFlags[i] = old[i];
//         },
//       });
//     }
//   } else {
//     const imageCount = 120;
//     const img = document.querySelector(".bottle-3-d img");
//     const imagePath = (i) => `/uploads/images/3d/jr-dark/${i.toString().padStart(2, "0")}.webp`;
//     const wrap = gsap.utils.wrap(0, imageCount);

//     const LOOPS = 6;
//     const START_INDEX = 82;

//     const frames = Array.from({ length: imageCount }, (_, i) => {
//       const im = new Image();
//       im.src = imagePath(i);
//       return im;
//     });

//     let targetIndex = START_INDEX;
//     let currentIndex = START_INDEX;
//     requestAnimationFrame(function raf() {
//       if (currentIndex !== targetIndex) {
//         currentIndex = targetIndex;
//         if (frames[currentIndex] && img) img.src = frames[currentIndex].src;
//       }
//       requestAnimationFrame(raf);
//     });

//     ScrollTrigger.create({
//       trigger: ".scene-2",
//       start: "top bottom",
//       end: "max",
//       scrub: true,
//       onUpdate: (self) => {
//         const rawIndex = Math.floor(self.progress * LOOPS * imageCount);
//         targetIndex = wrap(rawIndex + START_INDEX);
//       },
//     });

//     gsap
//       .timeline({
//         scrollTrigger: {
//           trigger: ".scene-2 .article_block_text",
//           start: "top 80%",
//           toggleActions: "play none none none", // never reverse
//           // markers: true,
//         },
//       })
//       // .to(split.chars, {
//       //   opacity: 1,
//       //   duration: 0.05,
//       //   stagger: 0.05,
//       //   ease: "none",
//       // });
//       .from(".scene-2 h4 .char", {
//         opacity: 0,
//         y: 10,
//         duration: 0.02,
//         stagger: 0.03,
//         ease: "power1.out",
//       });

//     // --- Pair paragraphs with videos inside a shared container ---
//     // Prefer .scene-2-text-container (p + video). Fallback to lone <p>s.
//     const pairs = gsap.utils.toArray(".scene-2 .scene-2-text-container").map((el) => {
//       return { el, p: el.querySelector("p"), v: el.querySelector("video") };
//     });

//     // If your markup has no wrappers, build pairs by nearest following video:
//     if (!pairs.length) {
//       const paras = gsap.utils.toArray(".scene-2 p");
//       paras.forEach((p) => {
//         const v = p.nextElementSibling?.tagName?.toLowerCase() === "video" ? p.nextElementSibling : null;
//         pairs.push({ el: p, p, v });
//       });
//     }

//     // --- Initial states ---
//     gsap.set(".scene-2 .article_block_item_img", { opacity: 1, y: 0 });
//     pairs.forEach(({ p, v }) => {
//       if (p) gsap.set(p, { opacity: 0, y: 20, willChange: "transform,opacity" });
//       if (v) {
//         v.pause?.();
//         v.currentTime = 0;
//         gsap.set(v, { opacity: 0 });
//       }
//     });

//     // --- One ScrollTrigger per pair, starting when the pair is 50% on screen ---
//     pairs.forEach(({ el, p, v }) => {
//       if (!p) return;

//       const tl = gsap.timeline({
//         scrollTrigger: {
//           trigger: el || p,
//           start: "top 80%", // pair’s center hits middle of viewport
//           toggleActions: "play none none none", // no reverse
//           // markers: true,
//         },
//       });

//       // 1) Paragraph in
//       tl.to(p, {
//         opacity: 1,
//         y: 0,
//         duration: 0.55,
//         ease: "power1.out",
//       });

//       // 2) Video after paragraph is fully revealed
//       if (v) {
//         tl.to(
//           v,
//           {
//             opacity: 1,
//             duration: 0.35,
//             ease: "power1.out",
//             onStart: () => {
//               v.currentTime = 0;
//               v.play?.().catch(() => {});
//             },
//           },
//           "+=0.05"
//         ); // tiny gap after paragraph finishes

//         // Optional: pause when the pair leaves the viewport window
//         ScrollTrigger.create({
//           trigger: el || p,
//           start: "top 100%",
//           end: "bottom 0%",
//           onLeave: () => v.pause?.(),
//           onLeaveBack: () => v.pause?.(),
//         });
//       }
//     });
//   }
// }
